import React from 'react';
import { motion } from 'framer-motion';
import { Target, Leaf, Cpu, Globe, Award, ShieldCheck, Zap } from 'lucide-react';
import SEO from '../components/common/SEO';

const About = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6">
      <SEO 
        title="About Us" 
        description="Learn about AgniThrust's mission to revolutionize the energy sector. We are a leading manufacturer and exporter of sustainable biomass products worldwide."
      />
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-24 px-4"
        >
          <span className="text-primary font-bold tracking-[0.2em] mb-4 block uppercase text-xs sm:text-sm">Our Legacy & Future</span>
          <h1 className="text-xl sm:text-3xl md:text-5xl lg:text-7xl font-black syne tracking-tighter mb-8 leading-[0.95] uppercase">
            AgniThrust <br />
            <span className="text-white/10 italic">Private Ltd.</span>
          </h1>
          <p className="text-slate-400 text-xl font-medium max-w-3xl mx-auto leading-relaxed">
            Founded with a vision to revolutionize the energy sector, AgniThrust is a leading manufacturer and exporter of biomass products. We bridge the gap between industrial energy needs and environmental sustainability.
          </p>
        </motion.div>

        {/* Vision/Mission Grid */}
        <div className="grid md:grid-cols-2 gap-12 mb-32">
           <div className="glass p-12 rounded-[60px] border border-white/10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-[60px] -z-10 group-hover:bg-primary/20 transition-all"></div>
              <Target className="text-primary mb-8" size={48} />
              <h3 className="text-3xl font-black syne mb-6 tracking-tight">Our Mission</h3>
              <p className="text-slate-400 text-lg leading-relaxed font-medium">
                 To provide sustainable and renewable energy solutions that replace fossil fuels. We strive for zero-emission energy production through innovative biomass technology, ensuring a cleaner planet for future generations.
              </p>
           </div>
           <div className="glass p-12 rounded-[60px] border border-white/10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber/10 blur-[60px] -z-10 group-hover:bg-amber/20 transition-all"></div>
              <Zap className="text-amber-500 mb-8" size={48} />
              <h3 className="text-3xl font-black syne mb-6 tracking-tight">Our Vision</h3>
              <p className="text-slate-400 text-lg leading-relaxed font-medium">
                 To become the global benchmark for bio-fuel excellence. We aim to power major industrial hubs worldwide with carbon-neutral pellets and bio-coal, driving the transition to a 100% renewable energy economy.
              </p>
           </div>
        </div>

        {/* Quality Section */}
        <div className="flex flex-col md:flex-row items-center gap-24 mb-32">
           <div className="flex-1 space-y-8 text-left">
              <h2 className="text-xl sm:text-3xl md:text-5xl lg:text-7xl font-black syne tracking-tighter transition-all">
                Uncompromising <br />
                <span className="text-primary">Quality.</span>
              </h2>
              <p className="text-slate-400 text-xl font-medium leading-relaxed">
                 Our production process follows rigorous international standards. From raw material sourcing to final pelletization, every step is monitored for consistency and calorific efficiency.
              </p>
              <div className="grid grid-cols-2 gap-8">
                 <div className="flex items-center gap-4">
                    <ShieldCheck className="text-primary" size={24} />
                    <span className="font-bold text-sm tracking-widest uppercase">Verified Certification</span>
                 </div>
                 <div className="flex items-center gap-4">
                    <Award className="text-primary" size={24} />
                    <span className="font-bold text-sm tracking-widest uppercase">Standard Excellence</span>
                 </div>
              </div>
           </div>
           <div className="flex-1 w-full aspect-square rounded-[80px] glass border border-white/10 overflow-hidden relative">
              <img src="/images/pellets.png" alt="Quality Control" className="w-full h-full object-cover scale-110 grayscale" />
              <div className="absolute inset-x-12 bottom-12 glass p-8 rounded-3xl border border-white/10 backdrop-blur-3xl">
                  <p className="text-xl font-black syne">99.9% Efficiency</p>
                 <p className="text-xs text-slate-500 font-bold uppercase tracking-[0.2em] mt-2">Tested Laboratory Standard</p>
              </div>
           </div>
        </div>

        {/* Features Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center pt-24 border-t border-white/5">
           {[
             { label: "Eco Friendly", icon: Leaf },
             { label: "High Calorie", icon: Zap },
             { label: "Advanced Tech", icon: Cpu },
             { label: "Global Presence", icon: Globe },
           ].map((item, i) => (
             <div key={i} className="space-y-4">
                <div className="w-16 h-16 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-primary mx-auto hover:bg-primary hover:text-white transition-all">
                   <item.icon size={28} />
                </div>
                <p className="font-black syne text-sm uppercase tracking-widest leading-none">{item.label}</p>
             </div>
           ))}
        </div>
      </div>
    </div>
  );
};

export default About;
